import { Button, VStack, Text, Flex } from '@chakra-ui/react';
import { NovaIconButton } from 'components/ui/NovaIconButton';
import { ReactElement } from 'react';
import { ModalClassTeacherRemovalVariantProps, SharedModalProps } from 'types/Modal';
import { useRemoveTeachers } from 'hooks/api/backend/managament/getClasses';
import { useQueryClient } from '@tanstack/react-query';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { toastContent } from 'config/toast';
import * as Sentry from '@sentry/react';
import { CloseIcon } from 'assets/icons/v2';
import { CustomModal } from '../CustomModal';
import { CustomModalContent } from '../CustomModalContent';

type Props = SharedModalProps & ModalClassTeacherRemovalVariantProps;

export const ClassTeacherRemovalModal = ({
  handleClose,
  isShown,
  teacherId,
  teacherName,
  classId,
}: Props): ReactElement => {
  const queryClient = useQueryClient();
  const toast = useNovaToast();
  const { mutateAsync: removeTeachers, isPending: isRemovingTeacher } = useRemoveTeachers();

  const handleRemove = async (): Promise<void> => {
    if (!teacherId || !classId) return;
    try {
      const updatedClass = await removeTeachers({ classId, ids: [teacherId] });
      // Update cache with the response data instead of refetching
      queryClient.setQueryData(['class', classId], updatedClass);
      queryClient.invalidateQueries({ queryKey: ['class-participants-search', classId] });

      toast.show({
        description: 'Lehrende erfolgreich entfernt',
        variant: ToastVariant.SUCCESS,
      });
      handleClose();
    } catch (error) {
      toast.show({ ...toastContent.error.default, variant: ToastVariant.ERROR });
      Sentry.captureException(error);
    }
  };

  return (
    <CustomModal isOpen={isShown} onClose={handleClose} placement="center" hideHeader size="md">
      <CustomModalContent p="30px">
        <Flex direction="column" gap="30px">
          <Flex direction="column" align="flex-end" gap="5px">
            <NovaIconButton
              aria-label="Schließen"
              variant="primary"
              size="l"
              onClick={handleClose}
              minW="24px"
              h="24px"
              p={0}
              icon={CloseIcon}
              iconProps={{ w: '24px', h: '24px', color: 'text.primary' }}
            />
            <VStack gap="15px" align="flex-start" w="full">
              <Text textStyle="h4.medium" color="text.primary">
                Lehrende entfernen
              </Text>
              <Text textStyle="body.m.regular" color="text.primary">
                Bist du sicher, dass du die Lehrperson {teacherName} aus dem Kurs entfernen
                möchtest?
              </Text>
            </VStack>
          </Flex>

          <Flex gap="l" justify="flex-end">
            <Button onClick={handleClose} variant="tertiary">
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleRemove} loading={isRemovingTeacher}>
              Entfernen
            </Button>
          </Flex>
        </Flex>
      </CustomModalContent>
    </CustomModal>
  );
};
